import { useCallback } from "react";
import { Form, FormGroup, FormField, Button } from "semantic-ui-react";

// Utils
import moment from "moment";
import { convertAdBeDateToMoment } from "react-lib/utils/dateUtils";

// Serializers
import ProxyPatientSerializer from "issara-sdk/types/ProxyPatientSerializer_apps_PRX";
import PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";

import { SelectedCustomerData } from "./types/MeBillingType";

// Types
type CustomerInfoHeaderProps = {
  customer: SelectedCustomerData | undefined;
  onEdit?: () => void;
  onLinkUserId?: () => void;
};

const IMAGES = {
  profile: "/static/images/person-placeholder.png",
  pencil: "/static/images/IH.png",
  id: "/static/images/meBilling/icon_id.png",
  message: "static/images/meBilling/icon_message.png",
  phone: "/static/images/meBilling/icon_phone.png",
  email: "/static/images/meBilling/icon_email.png",
  birthday: "/static/images/meBilling/icon_birthday.png",
  address: "/static/images/meBilling/icon_address.png",
  edit: "/static/images/meBilling/icon_edit.png",
};

const GENDER_MAPPED: Record<"" | "M" | "F" | "U" | "PM" | "PF" | "MM" | "TG", string> = {
  M: "ชาย",
  F: "หญิง",
  U: "ไม่ระบุ",
  MM: "MM",
  PF: "PF",
  PM: "PM",
  TG: "TG",
  "": "",
};

// --- Helper Func
export const calculateAge = (birthdate: string | undefined | null): string => {
  if (!birthdate) return "";

  const birth = convertAdBeDateToMoment(birthdate);
  if (!birth?.isValid()) return "";

  const now = moment();

  const years = now.diff(birth, "years");
  const birthAfterYears = birth.clone().add(years, "years");

  const months = now.diff(birthAfterYears, "months");
  const birthAfterMonths = birthAfterYears.clone().add(months, "months");

  const days = now.diff(birthAfterMonths, "days");

  return `${years} ปี ${months} เดือน ${days} วัน`;
};

export const formatBirthdateAD = (birthdate: string | undefined | null): string => {
  if (!birthdate) return "";

  const m = convertAdBeDateToMoment(birthdate);
  if (!m?.isValid()) return "";

  return m.format("DD/MM/YYYY");
};

type InfoItemProps = {
  icon?: string;
  iconAlt?: string;
  label?: string;
  labelColor?: string;
  value: string;
};

const InfoItem = ({ icon, iconAlt, label, labelColor, value }: InfoItemProps) => (
  <div style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "13px" }}>
    {icon && (
      <img
        style={{ width: "16px", height: "16px", objectFit: "contain" }}
        alt={iconAlt || "icon"}
        src={icon}
      />
    )}
    {label && (
      <span style={{ color: labelColor, fontWeight: labelColor ? "bold" : "normal" }}>{label}</span>
    )}
    <span>{value}</span>
  </div>
);

const CustomerInfoHeader = (props: CustomerInfoHeaderProps) => {
  const { customer, onEdit, onLinkUserId } = props;

  const handleEdit = useCallback(() => {
    onEdit?.();
  }, [onEdit]);

  const handleLinkUserId = useCallback(() => {
    onLinkUserId?.();
  }, [onLinkUserId]);
  
  return (
    <div
      style={{
        display: "flex",
        // alignItems: "center",
        backgroundColor: "rgba(255, 230, 230, 0.35)",
        margin: "0 15px",
        padding: "15px 5px",
      }}
    >
      {/* --- Profile Image Section --- */}
      <div
        style={{
          width: "5%",
          padding: "0 5px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <img
          style={{
            width: "50px",
            height: "50px",
            borderRadius: "50%",
            objectFit: "cover",
          }}
          alt="profile"
          src={customer?.patientDetail?.profile_image?.image || IMAGES.profile}
        />
      </div>

      {/* --- Info Section --- */}
      <div
        style={{
          width: "90%",
          padding: "0 5px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Form style={{ width: "100%" }}>
          <FormGroup inline>
            <FormField width={3}>
              <label>
                {customer?.proxyPatientAdmin?.proxy_patient}{" "}
                <span style={{ fontWeight: "normal" }}>
                  {customer?.patientDetail?.full_name || ""}
                </span>
              </label>
            </FormField>

            <FormField>
              <div style={{ width: "100%" }}>
                <InfoItem
                  value={`เพศ : ${GENDER_MAPPED[customer?.patientDetail?.gender || ""] || "-"}`}
                />
              </div>
            </FormField>

            <FormField>
              <InfoItem
                icon={IMAGES.id}
                iconAlt="id"
                value={customer?.patientDetail?.citizen_no || "-"}
              />
            </FormField>

            <FormField>
              <InfoItem
                icon={IMAGES.message}
                iconAlt="message"
                label="LineID:"
                value={customer?.patientDetail?.line_id || "-"}
              />
            </FormField>

            <FormField>
              <div style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "13px" }}>
                <img
                  style={{ width: "16px", height: "16px" }}
                  alt="birthday"
                  src={IMAGES.birthday}
                />
                <span>{formatBirthdateAD(customer?.patientDetail?.birthdate)}</span>
                <span>[{calculateAge(customer?.patientDetail?.birthdate)}]</span>
              </div>
            </FormField>

            <FormField>
              <InfoItem
                icon={IMAGES.phone}
                iconAlt="phone"
                value={customer?.patientDetail?.present_address?.tel_mobile || "-"}
              />
            </FormField>

            <FormField>
              <InfoItem
                icon={IMAGES.email}
                iconAlt="email"
                value={customer?.patientDetail?.email || "-"}
              />
            </FormField>

            <FormField style={{ flex: 1 }} />
          </FormGroup>

          <FormGroup inline style={{ marginBottom: 0 }}>
            <FormField width={3}>
              <div style={{ width: "100%" }}>
                <Button
                  // todo --- Logic การแสดงผลกรณีผูก ID แล้ว
                  size="mini"
                  style={{
                    border: "solid 1px var(--primary-theme-color)",
                    backgroundColor: "#FFE6E6",
                    padding: "5px 10px",
                    width: "fit-content",
                  }}
                  onClick={handleLinkUserId}
                >
                  เชื่อมไอดีผู้ใช้งาน
                </Button>
              </div>
            </FormField>

            <FormField>
              <InfoItem
                icon={IMAGES.address}
                iconAlt="address"
                value={customer?.proxyPatientAdmin?.patient_detail?.present_address_full || ""}
              />
            </FormField>
          </FormGroup>
        </Form>
      </div>

      {/* --- Edit Section --- */}
      <div
        style={{
          width: "5%",
          padding: "0 5px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {onEdit && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: "rgba(255, 229, 229, 1)",
              cursor: "pointer",
              width: "35px",
              height: "35px",
            }}
            onClick={handleEdit}
          >
            <img style={{ width: "20px", height: "20px" }} alt="edit" src={IMAGES.edit} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerInfoHeader;
